import { type FileKind, kindOf } from "./files.ts";

export const ANCHOR_KINDS = ["box", "time", "lines", "cells"] as const;
export type AnchorKind = (typeof ANCHOR_KINDS)[number];

export type Box = { kind: "box"; x: number; y: number; w: number; h: number };

export type Time = { kind: "time"; from: number; to: number };

export type Lines = { kind: "lines"; from: number; to: number };

export type Cells = { kind: "cells"; from: string; to: string };

export type Anchor = Box | Time | Lines | Cells;

export function anchorFor(kind: FileKind): AnchorKind | null {
  if (kind === "Image") return "box";
  if (kind === "Video" || kind === "Audio") return "time";
  if (kind === "Text") return "lines";
  if (kind === "Data") return "cells";
  return null;
}

export function anchorInto(mediaType: string): AnchorKind | null {
  return anchorFor(kindOf(mediaType));
}

class BadAnchor extends Error {
  override readonly name = "BadAnchor";
}

function number(from: Record<string, unknown>, name: string): number {
  const value = from[name];
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new BadAnchor(`anchor.${name} is a number.`);
  return value;
}

function fraction(from: Record<string, unknown>, name: string): number {
  const value = number(from, name);
  if (value < 0 || value > 1) throw new BadAnchor(`anchor.${name} is between 0 and 1.`);
  return value;
}

function whole(from: Record<string, unknown>, name: string): number {
  const value = number(from, name);
  if (!Number.isInteger(value) || value < 1) throw new BadAnchor(`anchor.${name} is a line number.`);
  return value;
}

function cell(from: Record<string, unknown>, name: string): string {
  const value = from[name];
  if (typeof value !== "string" || !/^[A-Z]{1,3}[1-9][0-9]{0,6}$/.test(value)) {
    throw new BadAnchor(`anchor.${name} is a cell like B2.`);
  }
  return value;
}

export function anchorOf(said: unknown): Anchor {
  if (said === null || typeof said !== "object" || Array.isArray(said)) {
    throw new BadAnchor("anchor is an object with a kind.");
  }
  const from = said as Record<string, unknown>;
  switch (from.kind) {
    case "box": {
      const box = {
        kind: "box" as const,
        x: fraction(from, "x"),
        y: fraction(from, "y"),
        w: fraction(from, "w"),
        h: fraction(from, "h"),
      };
      if (box.w <= 0 || box.h <= 0) throw new BadAnchor("anchor is a region with a size.");
      if (box.x + box.w > 1 || box.y + box.h > 1) throw new BadAnchor("anchor is inside the image.");
      return box;
    }
    case "time": {
      const at = { kind: "time" as const, from: number(from, "from"), to: number(from, "to") };
      if (at.from < 0 || at.to < at.from) throw new BadAnchor("anchor ends after it starts.");
      return at;
    }
    case "lines": {
      const at = { kind: "lines" as const, from: whole(from, "from"), to: whole(from, "to") };
      if (at.to < at.from) throw new BadAnchor("anchor ends after it starts.");
      return at;
    }
    case "cells":
      return { kind: "cells", from: cell(from, "from"), to: cell(from, "to") };
    default:
      throw new BadAnchor(`anchor.kind is one of ${ANCHOR_KINDS.join(", ")}.`);
  }
}

function mark(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export function saysWhere(anchor: Anchor): string {
  switch (anchor.kind) {
    case "box":
      return "a region";
    case "time":
      return anchor.from === anchor.to ? mark(anchor.from) : `${mark(anchor.from)}-${mark(anchor.to)}`;
    case "lines":
      return anchor.from === anchor.to ? `line ${anchor.from}` : `lines ${anchor.from}-${anchor.to}`;
    case "cells":
      return anchor.from === anchor.to ? anchor.from : `${anchor.from}:${anchor.to}`;
  }
}
